/**
 * FZLCManagementHub — iter-255 (Feb 2026)
 * =======================================
 * Third top-level card inside the CLOUDGM FZ LLC (للتحكم) view, alongside
 * "منصة العملاء" and "منصة التجار". Now hosts the "Dropdown Lists" wrapper
 * card (iter-255) which groups all catalog-related dropdown editors.
 *
 * Path: لوحة التحكم → التحكم والمعلومات للمنصات → CLOUDGM FZ LLC (للتحكم)
 *       → إدارة الشركة CLOUDGM FZ-LLC
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, ListChecks } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { usePerms } from '../permissions/PermissionsContext';

export default function FZLCManagementHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const breadcrumb = [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    { label_ar: 'إدارة الشركة CLOUDGM FZ-LLC', label_en: 'Manage CLOUDGM FZ-LLC', path: null },
  ];

  const cards = [
    {
      id: 'dropdown-lists',
      title_ar: 'القوائم المنسدلة لإدارة الشركة',
      title_en: 'Company Management Dropdown Lists',
      desc_ar: 'كل القوائم المنسدلة والكتالوجات المستخدمة داخل ملفات الشركات (خدمات الشركات، أنواع الخدمات، وأكثر).',
      desc_en: 'Every dropdown & catalog used inside company files (services, service types, and more).',
      icon: ListChecks,
      color: '#a855f7',
      path: '/control/cloudgm-fzlc-management/dropdowns',
      testid: 'fzlc-mgmt-card-dropdown-lists',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader onBack={() => navigate('/dashboard?view=platform-control&sub=fzlc')} />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="h-11 w-11 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[#d4af37]" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="fzlc-mgmt-title">
                {t('إدارة الشركة CLOUDGM FZ-LLC', 'Manage CLOUDGM FZ-LLC')}
              </h1>
            </div>
            <p className="text-[#808080] max-w-2xl mx-auto">
              {t(
                'أدر القوائم والكتالوجات المشتركة بين المنصات — تظهر في كل الفورمات والأزرار داخل ملفات الشركات.',
                'Manage the shared lists & catalogs used across every platform — surfaced in every company-file form and button.',
              )}
            </p>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {visiblePages(cards, 'path').map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.button
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -6, scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(c.path)}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center overflow-hidden transition-all hover:bg-white/[0.04] cursor-pointer"
                  data-testid={c.testid}
                >
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: `inset 0 0 0 1px ${c.color}60, 0 8px 32px ${c.color}20` }}
                  />
                  <div className="relative">
                    <div
                      className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center border transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}30` }}
                    >
                      <Icon className="h-10 w-10" style={{ color: c.color }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-white">
                      {isAr ? c.title_ar : c.title_en}
                    </h3>
                    <p className="text-xs text-[#808080] px-2">
                      {isAr ? c.desc_ar : c.desc_en}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
